import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Shield, Truck, Warning, CheckCircle, Package, ArrowSquareOut,
  Receipt, CurrencyDollar, ArrowCounterClockwise, Cube, ArrowsClockwise,
} from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Per-escrow-state buckets shown as collapsible sections.
const BUCKETS = [
  { key: 'held',     label: 'In escrow',  color: '#007AFF', icon: Shield      },
  { key: 'released', label: 'Shipped',    color: '#10B981', icon: Truck       },
  { key: 'disputed', label: 'Disputed',   color: '#EF4444', icon: Warning     },
  { key: 'refunded', label: 'Refunded',   color: '#94A3B8', icon: ArrowCounterClockwise },
  { key: 'canceled', label: 'Canceled',   color: '#64748B', icon: Warning     },
];

// Bucket assignment — escrow_state first, fall back to status for canceled.
const bucketOf = (order) => {
  if (order.status === 'canceled') return 'canceled';
  return order.escrow_state || 'held';
};

const BuyerAccountDialog = ({ open, onOpenChange, address, onDispute }) => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/agent/orders/wallet/${address}`);
      setOrders(r.data.orders || []);
    } catch (_e) {
      toast.error('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, address]);

  // Totals across ALL orders for this wallet
  const totals = orders.reduce((acc, o) => {
    const amt = Number(o.total_usd || 0);
    const qty = Number(o.total_qty || 0);
    acc.orders += 1;
    acc.items  += qty;
    acc.revenue += amt;
    if ((o.escrow_state || '') === 'refunded') acc.refunds += amt;
    return acc;
  }, { orders: 0, items: 0, revenue: 0, refunds: 0 });

  // Group orders by bucket
  const byBucket = BUCKETS.reduce((acc, b) => {
    acc[b.key] = orders.filter(o => bucketOf(o) === b.key);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B1120] border-[#1E293B] text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="buyer-account-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['JetBrains_Mono'] text-cyan-300 flex items-center gap-2">
            <Shield size={20} weight="fill" /> Your account
          </DialogTitle>
          <DialogDescription className="text-xs text-[#94A3B8] break-all">
            Orders for wallet <code className="text-cyan-300">{address}</code>
          </DialogDescription>
        </DialogHeader>

        {/* Totals grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Totals label="Total revenue" value={`$${totals.revenue.toFixed(2)}`} color="#10B981" Icon={CurrencyDollar} testid="acct-total-revenue" />
          <Totals label="Total refunds" value={`$${totals.refunds.toFixed(2)}`} color="#EF4444" Icon={ArrowCounterClockwise} testid="acct-total-refunds" />
          <Totals label="Total orders"  value={totals.orders} color="#007AFF" Icon={Receipt} testid="acct-total-orders" />
          <Totals label="Total items"   value={totals.items}  color="#8B5CF6" Icon={Cube}    testid="acct-total-items" />
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-[#94A3B8]">{loading ? 'Loading…' : `${orders.length} order${orders.length === 1 ? '' : 's'}`}</p>
          <button
            onClick={load}
            className="text-xs text-[#94A3B8] hover:text-white flex items-center gap-1 font-['JetBrains_Mono']"
            data-testid="acct-refresh"
          >
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </div>

        {/* Buckets */}
        {!loading && orders.length === 0 && (
          <EmptyState />
        )}

        {BUCKETS.map(b => {
          const list = byBucket[b.key] || [];
          if (list.length === 0) return null;
          const Icon = b.icon;
          return (
            <section key={b.key} data-testid={`acct-bucket-${b.key}`} className="space-y-2">
              <header className="flex items-center gap-2 pt-3">
                <Icon size={14} style={{ color: b.color }} weight="fill" />
                <h3 className="text-xs uppercase font-['JetBrains_Mono']" style={{ color: b.color }}>
                  {b.label} <span className="text-[#64748B]">· {list.length}</span>
                </h3>
              </header>
              <div className="space-y-2">
                {list.map(o => (
                  <OrderRow key={o.id} order={o} bucket={b} onDispute={onDispute} />
                ))}
              </div>
            </section>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

const Totals = ({ label, value, color, Icon, testid }) => (
  <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase font-['JetBrains_Mono']" style={{ color }}>
      <Icon size={11} weight="fill" /> {label}
    </div>
    <p className="text-xl font-['JetBrains_Mono'] font-bold mt-1">{value}</p>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-10">
    <Package size={36} className="mx-auto text-[#334155]" />
    <p className="text-sm text-[#94A3B8] mt-2 font-['JetBrains_Mono']">No orders yet</p>
    <p className="text-xs text-[#64748B]">Your on-chain orders will appear here automatically.</p>
  </div>
);

const OrderRow = ({ order, bucket, onDispute }) => {
  const created = order.created_at ? new Date(order.created_at).toLocaleDateString() : '';
  const canDispute = (order.escrow_state === 'held');
  return (
    <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
         data-testid={`acct-order-${order.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-cyan-300 font-mono truncate" title={order.id}>#{order.id.slice(0, 12)}…</p>
        <p className="text-sm text-white font-['JetBrains_Mono']">
          ${Number(order.total_usd || 0).toFixed(2)} <span className="text-[#64748B] text-xs">· {order.total_qty} items · {created}</span>
        </p>
        {order.tracking_number && (
          <p className="text-[10px] text-[#10B981] font-mono">Tracking: {order.tracking_number}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <a
          href={`/order/${order.id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-white border border-[#334155] rounded px-2 py-1"
          data-testid={`acct-view-${order.id}`}
        >
          View <ArrowSquareOut size={10} />
        </a>
        {canDispute && (
          <Button
            onClick={() => onDispute?.(order.id)}
            size="sm"
            className="bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs h-7"
            data-testid={`acct-dispute-${order.id}`}
          >
            <Warning size={12} weight="fill" className="mr-1" /> Open dispute
          </Button>
        )}
        {bucket.key === 'released' && !canDispute && (
          <span className="inline-flex items-center gap-1 text-[10px] text-[#10B981]">
            <CheckCircle size={10} weight="fill" /> Complete
          </span>
        )}
      </div>
    </div>
  );
};

export default BuyerAccountDialog;
